import React, { memo, useEffect, useMemo, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Select, { components, type ValueType } from '@atlaskit/select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import type { FieldBaseProps } from '../common/types';

import type { JiraPriorityField_Platform$key } from './__generated__/JiraPriorityField_Platform.graphql';

const messages = defineMessages({
	selectLabel: {
		id: 'jira.jira.jira-platform-fields.priority.select.label',
		defaultMessage: 'Select issue priority',
		description: 'Label for the Jira priority select dropdown',
	},
});

const styles = cssMap({
	control: {
		width: '100%',
		boxSizing: 'border-box',
		height: '40px',
	},
	container: {
		display: 'flex',
		flex: '1 1 0',
		minWidth: '0px',
		width: '100%',
	},
	valueContainer: {
		minWidth: '0px',
	},
	inline: {
		width: '100%',
	},
	readOnlyViewContainer: {
		paddingLeft: token('space.100'),
		display: 'flex',
		alignItems: 'center',
		height: '40px',
		width: '100%',
	},
});

type PriorityOption = {
	iconUrl?: string | null;
	label: string;
	value: string;
};

const PriorityOptionComponent = (option: PriorityOption) => {
	return (
		<Inline space="space.050" alignBlock="center" xcss={styles.inline}>
			<Image
				width={16}
				src={option.iconUrl ?? undefined}
				alt={
					FeatureGates.getExperimentValue('jira-aifc-a11y-fixes', 'isEnabled', false, {
						fireExperimentExposure: false,
					})
						? ''
						: option.label
				}
				aria-hidden={
					FeatureGates.getExperimentValue('jira-aifc-a11y-fixes', 'isEnabled', false, {
						fireExperimentExposure: false,
					})
						? true
						: undefined
				}
			/>
			<Text maxLines={1}>{option.label}</Text>
		</Inline>
	);
};

export type Props = FieldBaseProps & {
	field: JiraPriorityField_Platform$key;
	onChange?: (newValue: string | null) => void;
};

/**
 * `JiraPriorityField` is a memoized React component that renders a Jira priority field,
 * supporting both editable and read-only modes. It displays the current priority and allows
 * users to select from available priorities if the field is editable. The component uses
 * Relay fragments to fetch priority data and renders options with icons and labels.
 *
 * @param field - The Relay fragment reference for the Jira priority field.
 * @param fieldValue - The currently selected priority value.
 * @param onChange - Callback invoked when the priority selection changes.
 * @returns A JSX element representing the priority field, or `null` if no priority is found.
 */
export const JiraPriorityField: React.MemoExoticComponent<
	({ field, fieldValue, onChange, isFieldEditable }: Props) => JSX.Element | null
> = memo(({ field, fieldValue, onChange, isFieldEditable = true }: Props): JSX.Element | null => {
	const data = useFragment<JiraPriorityField_Platform$key>(
		graphql`
			fragment JiraPriorityField_Platform on JiraPriorityField {
				fieldConfig {
					isEditable
				}
				priority {
					priorityId
					name
					iconUrl
				}
				priorities {
					edges {
						node {
							priorityId
							name
							iconUrl
						}
					}
				}
			}
		`,
		field,
	);

	const { formatMessage } = useIntl();

	const selectRef = useRef<AtlaskitSelectRefType | null>(null);

	const isEditable = data?.fieldConfig?.isEditable && isFieldEditable;
	const defaultValue = data?.priority?.name;

	const options: PriorityOption[] = useMemo(() => {
		return (
			data?.priorities?.edges?.filter(Boolean).map((edge) => ({
				value: String(edge?.node?.priorityId ?? ''),
				label: String(edge?.node?.name ?? ''),
				iconUrl: edge?.node?.iconUrl ?? undefined,
			})) ?? []
		);
	}, [data?.priorities?.edges]);

	const handleChange = (newValue: ValueType<PriorityOption>) => {
		onChange?.(newValue?.label ?? null);
		// Defer blur so internal state settles before removing focus styling.
		queueMicrotask(() => selectRef.current?.blur?.());
	};

	const handleMenuClose = () => {
		selectRef.current?.blur?.();
	};

	const selectedOption = useMemo(() => {
		if (fieldValue == null) {
			return null;
		}
		if (fieldValue) {
			const fieldOption = options.find((option) => option.label === fieldValue) ?? null;
			return fieldOption;
		}
	}, [fieldValue, options]);

	// Allows us to make sure we only fall back to default value once, on initial render
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		// If the passed down value is empty and a default value is available, tell the upstream
		if (!selectedOption?.label && defaultValue != null && isFirstRenderRef.current) {
			onChange?.(defaultValue);
		}
		isFirstRenderRef.current = false;
	}, [selectedOption?.label, defaultValue, onChange]);

	if (isEditable) {
		return (
			<Select<PriorityOption>
				ref={selectRef}
				label={formatMessage(messages.selectLabel)}
				appearance="subtle"
				options={options}
				getOptionValue={(option) => option.value}
				getOptionLabel={(option) => option.label}
				value={selectedOption}
				onChange={handleChange}
				onMenuClose={handleMenuClose}
				searchThreshold={5}
				formatOptionLabel={PriorityOptionComponent}
				components={{
					Control: (props) => <components.Control {...props} xcss={styles.control} />,
					SelectContainer: (props) => (
						<components.SelectContainer {...props} xcss={styles.container} />
					),
					ValueContainer: (props) => (
						<components.ValueContainer {...props} xcss={styles.valueContainer} />
					),
					DropdownIndicator: (props) =>
						props.selectProps.menuIsOpen ? <components.DropdownIndicator {...props} /> : null,
				}}
				isMulti={false}
				menuPortalTarget={
					fg('gravityai-4581-jira-aifc-iframe-korosu') ? undefined : getDocument()?.body
				}
				menuPosition={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'fixed' : 'absolute'}
				menuPlacement="auto"
				styles={
					fg('gravityai-4581-jira-aifc-iframe-korosu')
						? {
								menu: (base) => ({ ...base, zIndex: layers.modal() }),
								menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
							}
						: undefined
				}
			/>
		);
	}

	if (!selectedOption) {
		return null;
	}

	return <Box xcss={styles.readOnlyViewContainer}>{PriorityOptionComponent(selectedOption)}</Box>;
});
